import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DialogDismissGuardDirective } from '@shared/ui';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { PipelineCategoryResult, PipelineCategoryState } from '../../../state/pipeline-category.state';

import { PipelineCategoryEditorComponent } from './pipeline-category-editor.component';

/**
 * Dialog host for `PipelineCategoryEditorComponent`.
 *
 * Thin wrapper. The consumer is expected to seed the shared
 * `PipelineCategoryState` (variant / readonly / methodType / initialCategory)
 * via `PipelineCategoryEffects.initialize` **before** opening the dialog, so
 * this host only bridges the state's `status$` / `result$` signals onto the
 * dialog's close payload. Closes with a `PipelineCategoryResult` on save, and
 * `undefined` on cancel.
 *
 * Backdrop click / Escape do not close the dialog directly:
 * `DialogDismissGuardDirective` routes them through the editor's `onCancel()`
 * (dirty-gated discard confirm, same as the Cancel button). `canDeactivate()`
 * covers navigation-driven dismissal (browser back): the routed pipeline
 * editor page consults open dialogs before allowing the exit, and this host
 * delegates the decision to the editor's own gate. Mounts without a routed
 * page underneath (e.g. the pipeline editor opened as a plain dialog) have no
 * route guard, so browser-back there still disposes the stack silently —
 * pre-existing limitation.
 *
 * The dialog does **not** declare its own providers. The consumer that opens
 * it must ensure `pipelineCategoryEditorProviders` (or compatible test stubs)
 * are reachable from the dialog's injector chain — pass `injector: this.injector`
 * to `MatDialog.open` so the consumer's providers propagate to the overlay.
 *
 * `labelStoreProviders` is intentionally NOT part of
 * `pipelineCategoryEditorProviders` (it lives at the outer
 * `pipelineEditorProviders` scope in production so the shared `LabelEffects`
 * instance is the same one used by the step / pipeline editor facades).
 * Standalone consumers must spread `labelStoreProviders` themselves on the
 * injector chain.
 */
@Component({
  selector: 'ts-pipeline-category-editor-dialog',
  template: `
    <div style="padding:16px;">
      <ts-pipeline-category-editor
        #editor
        tsDialogDismissGuard
        (dismissRequested)="editor.onCancel()"
      ></ts-pipeline-category-editor>
    </div>
  `,
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatDialogModule, PipelineCategoryEditorComponent, DialogDismissGuardDirective],
})
export class PipelineCategoryEditorDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @ViewChild(PipelineCategoryEditorComponent)
  private editor: PipelineCategoryEditorComponent | undefined;

  constructor(
    @Optional()
    public readonly dialogRef: MatDialogRef<
      PipelineCategoryEditorDialogComponent,
      PipelineCategoryResult | undefined
    > | null,
    private readonly state: PipelineCategoryState
  ) {}

  /** Exit gate consulted by the routed page host before navigation commits. */
  public canDeactivate(): Observable<boolean> | boolean {
    return this.editor ? this.editor.canDeactivate() : true;
  }

  ngOnInit(): void {
    this.state.status$.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      if (status === 'idle') return;
      if (status === 'cancelled') {
        this.dialogRef?.close(undefined);
        return;
      }
      this.state.result$
        .pipe(take(1), takeUntil(this.destroy$))
        .subscribe((result) => this.dialogRef?.close(result ?? undefined));
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
